import { SlideHandle, useCarousel } from 'nuka-carousel';
import { ReactNode, useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { Button } from '@calm-web/design-system';

import { DiscountLandingProps } from '@/components/discountLanding/DiscountLandingWrapper/types';
import Image from '@/components/image/NextImage';
import GridWrapper from '@/components/ui/GridWrapper';
import ChevronLeft from 'icons/chevron-left-aligned.svg';
import ChevronRight from 'icons/chevron-right-aligned.svg';
import StarsIcon from 'icons/five-stars-orange.svg';

import { testimonialItems } from './data';
import messages from './messages';
import {
	Arrow,
	AuthorName,
	AuthorRow,
	AuthorThumbnail,
	ButtonWrapper,
	Carousel,
	Heading,
	JoinStat,
	MemberSince,
	MobileTestimonialItems,
	Nav,
	NavDot,
	QuoteIcon,
	StarsWrapper,
	TestimonialItem,
	TestimonialItems,
	TestimonialItemsGrid,
	TestimonialQuote,
	Wrapper,
} from './styles';

const CarouselItem = ({ item, index }: { item: (typeof testimonialItems)[number]; index: number }) => {
	const { formatMessage } = useIntl();
	const { currentPage } = useCarousel();

	return (
		<TestimonialItem isActive={currentPage === index}>
			<QuoteIcon>“</QuoteIcon>
			<TestimonialQuote>
				{formatMessage(item.testimonial, {
					boldtext: (chunks: ReactNode) => <strong>{chunks}</strong>,
				})}
			</TestimonialQuote>
			<AuthorRow>
				<AuthorThumbnail>
					<Image
						width={48}
						height={48}
						src={item.thumbnail}
						alt={`Picture for ${formatMessage(item.name)}`}
					/>
				</AuthorThumbnail>
				<div>
					<AuthorName>{formatMessage(item.name)}</AuthorName>
					<MemberSince>{formatMessage(item.memberSince)}</MemberSince>
				</div>
			</AuthorRow>
		</TestimonialItem>
	);
};

const Testimonials = ({ onClick }: DiscountLandingProps) => {
	const { formatMessage } = useIntl();
	const [currentSlide, setCurrentSlide] = useState(0);

	const carouselRef = useRef<SlideHandle>(null);

	const onNext = useCallback(() => {
		setCurrentSlide(_slide => (_slide + 1) % testimonialItems.length);
		carouselRef.current?.goForward();
	}, []);

	const onPrev = useCallback(() => {
		setCurrentSlide(_slide => (_slide === 0 ? testimonialItems.length - 1 : _slide - 1));
		carouselRef.current?.goBack();
	}, []);

	const onDrag = useCallback(
		(slideIndex: number) => {
			// As this callback happens after an animation, we need to make sure that we ignore "old" updates
			setCurrentSlide(_slide => (_slide !== currentSlide ? _slide : slideIndex));
		},
		[currentSlide],
	);

	const goToSlide = useCallback((slideIndex: number) => {
		setCurrentSlide(slideIndex);
		carouselRef.current?.goToPage(slideIndex);
	}, []);

	return (
		<Wrapper>
			<GridWrapper columns={3}>
				<Heading>{formatMessage(messages.heading)}</Heading>
				<JoinStat>{formatMessage(messages.downloads)}</JoinStat>
				<JoinStat>{formatMessage(messages.starRewards)}</JoinStat>
				<StarsWrapper>
					<StarsIcon />
				</StarsWrapper>
				{testimonialItems.length > 0 && (
					<>
						<TestimonialItems>
							<TestimonialItemsGrid allowOverflow columns={3}>
								{testimonialItems.map(item => {
									return (
										<TestimonialItem key={formatMessage(item.name)}>
											<QuoteIcon>“</QuoteIcon>
											<TestimonialQuote>
												{formatMessage(item.testimonial, {
													boldtext: (chunks: ReactNode) => <strong>{chunks}</strong>,
												})}
											</TestimonialQuote>
											<AuthorRow>
												<AuthorThumbnail>
													<Image
														width={48}
														height={48}
														src={item.thumbnail}
														alt={`Picture for ${formatMessage(item.name)}`}
													/>
												</AuthorThumbnail>
												<div>
													<AuthorName>{formatMessage(item.name)}</AuthorName>
													<MemberSince>{formatMessage(item.memberSince)}</MemberSince>
												</div>
											</AuthorRow>
										</TestimonialItem>
									);
								})}
							</TestimonialItemsGrid>
						</TestimonialItems>
						<MobileTestimonialItems>
							<Carousel ref={carouselRef} afterSlide={onDrag}>
								{testimonialItems.map((item, index) => (
									<CarouselItem key={formatMessage(item.name)} item={item} index={index} />
								))}
							</Carousel>
							<Nav>
								<Arrow as={ChevronLeft} onClick={onPrev} />
								{testimonialItems.map((item, index) => (
									<NavDot
										isActive={currentSlide === index}
										key={`nav-dot${formatMessage(item.name)}`}
										onClick={(): void => goToSlide(index)}
									/>
								))}
								<Arrow as={ChevronRight} onClick={onNext} />
							</Nav>
						</MobileTestimonialItems>
					</>
				)}
				<ButtonWrapper>
					<Button
						fullWidth
						onPress={() =>
							onClick({
								section: 'testimonials',
								copy: messages.cta.defaultMessage,
							})
						}
						backgroundColor="white"
						textColor="gray8"
					>
						{formatMessage(messages.cta)}
					</Button>
				</ButtonWrapper>
			</GridWrapper>
		</Wrapper>
	);
};

export default Testimonials;
